import { useRef, useCallback, useEffect } from "react";
import { getPrewarmedObjectDetection, prewarmObjectDetection } from "../utils/interviewPrep";

// Objects that trigger violations and their severity
const FLAGGED_OBJECTS = {
  "cell phone": { severity: "high", label: "Phone detected" },
  book: { severity: "medium", label: "Book detected" },
  laptop: { severity: "high", label: "Secondary device detected" },
  remote: { severity: "low", label: "Remote detected" },
  // tablet shows as laptop/cell phone in COCO-SSD — best effort
};

const MIN_SCORE = 0.65; // ignore low-confidence detections
const MIN_PERSON_AREA = 0.08; // person box must be >8% of frame area to count
const PERSON_COOLDOWN = 10000; // ms between multiple_persons alerts
const OBJECT_COOLDOWN = 15000; // ms between same-object alerts

export function useObjectDetection({ onViolation, enabled = true }) {
  const modelRef = useRef(null);
  const loadingRef = useRef(false);
  const detectingRef = useRef(false); // prevents overlapping runs
  const frameCountRef = useRef(0);
  const lastAlertRef = useRef({}); // { violationType: lastTimestamp }

  // Lazy-load or adopt pre-warmed model
  const loadModel = useCallback(async () => {
    if (modelRef.current) return modelRef.current;

    // Check if pre-warmed model is already available from interviewPrep
    const prewarmed = getPrewarmedObjectDetection();
    if (prewarmed) {
      modelRef.current = prewarmed;
      console.log("✅ Using pre-warmed COCO-SSD model (zero-latency)");
      return modelRef.current;
    }

    if (loadingRef.current) return modelRef.current;
    loadingRef.current = true;
    try {
      const model = await prewarmObjectDetection();
      if (model) {
        modelRef.current = model;
        console.log("✅ COCO-SSD object detection model loaded and warmed");
      }
    } catch (e) {
      console.warn("⚠️ COCO-SSD failed to load:", e);
    } finally {
      loadingRef.current = false;
    }
    return modelRef.current;
  }, []);

  useEffect(() => {
    if (enabled) {
      // Adopt pre-warmed model immediately if available, or initialize in background
      const prewarmed = getPrewarmedObjectDetection();
      if (prewarmed) {
        modelRef.current = prewarmed;
      } else {
        void loadModel();
      }
    }
  }, [enabled, loadModel]);

  const isCooledDown = useCallback((type) => {
    const cooldown =
      type === "multiple_persons" ? PERSON_COOLDOWN : OBJECT_COOLDOWN;
    const last = lastAlertRef.current[type] || 0;
    return Date.now() - last > cooldown;
  }, []);

  const markAlerted = useCallback((type) => {
    lastAlertRef.current[type] = Date.now();
  }, []);

  // Called from ProctoringMonitor's onFrame — every 30th frame
  const detectFrame = useCallback(
    async (videoElement, faceCount = 0) => {
      if (!enabled || !videoElement) return;

      frameCountRef.current += 1;
      if (frameCountRef.current % 30 !== 0) return;
      if (detectingRef.current) return;

      const model = await loadModel();
      if (!model) return;

      detectingRef.current = true;
      try {
        const predictions = await model.detect(videoElement);

        const frameArea =
          videoElement.videoWidth * videoElement.videoHeight || 1;
        let personCount = 0;

        for (const pred of predictions) {
          if (pred.score < MIN_SCORE) continue;

          const className = pred.class.toLowerCase();

          if (className === "person") {
            const [, , w, h] = pred.bbox;
            const area = (w * h) / frameArea;
            if (area >= MIN_PERSON_AREA) personCount += 1;
            continue;
          }

          if (FLAGGED_OBJECTS[className]) {
            const { severity, label } = FLAGGED_OBJECTS[className];
            const violationType = "object_detected";
            const alertKey = `${violationType}_${className}`;
            if (isCooledDown(alertKey)) {
              markAlerted(alertKey);
              onViolation?.({
                type: violationType,
                severity,
                message: label,
                details: {
                  object_class: className,
                  confidence: Math.round(pred.score * 100),
                },
              });
            }
          }
        }

        if (personCount >= 2) {
          const faceAgreement = faceCount >= 2;
          if (faceAgreement && isCooledDown("multiple_persons")) {
            markAlerted("multiple_persons");
            onViolation?.({
              type: "multiple_persons",
              severity: "high",
              message: "Multiple people detected in frame",
              details: {
                person_count: personCount,
                face_count: faceCount,
              },
            });
          }
        }
      } catch (e) {
        console.warn("Object detection error:", e);
      } finally {
        detectingRef.current = false;
      }
    },
    [enabled, loadModel, onViolation, isCooledDown, markAlerted]
  );

  const cleanup = useCallback(() => {
    modelRef.current = null;
    loadingRef.current = false;
    detectingRef.current = false;
    frameCountRef.current = 0;
    lastAlertRef.current = {};
  }, []);

  return { detectFrame, cleanup };
}
